import { Link } from '@tanstack/react-router';
import type { AgeState } from '@/lib/format';

export interface Segment {
  id: string;
  label: string;
  href: string;
}

export interface TopBarProps {
  title: string;
  segments?: readonly Segment[];
  activeSegment?: string;
  onSearch: () => void;
  ageState: AgeState;
  ageText: string;
  onRefresh?: () => void;
  inboxCount: number;
}

/**
 * 48px across the content area: title and sub-nav on the left; search, the global data-age
 * chip and the inbox bell on the right.
 *
 * There is no production toggle here. Gate and BOGO live in Settings > Controls behind a
 * tier 3 confirmation, and nothing in this bar can change the storefront.
 */
export function TopBar({
  title,
  segments,
  activeSegment,
  onSearch,
  ageState,
  ageText,
  onRefresh,
  inboxCount,
}: TopBarProps) {
  const ageClass =
    ageState === 'fresh' || ageState === 'unknown' ? 'o-age' : `o-age o-age--${ageState}`;

  return (
    <header className="o-topbar">
      <h1 className="o-topbar__title">{title}</h1>

      {segments && segments.length > 0 ? (
        <div className="o-segments" data-allow-hscroll="true" role="tablist" aria-label={`${title} sections`}>
          {segments.map((segment) => (
            <Link
              key={segment.id}
              to={segment.href}
              className="o-chip"
              role="tab"
              aria-selected={segment.id === activeSegment}
              {...(segment.id === activeSegment ? { 'aria-current': 'page' as const } : {})}
            >
              {segment.label}
            </Link>
          ))}
        </div>
      ) : null}

      <span className="o-topbar__spacer" />

      <button type="button" className="o-topbar__search" onClick={onSearch}>
        <span>Search orders, customers, SKUs, tracking</span>
        <kbd>Ctrl K</kbd>
        <span className="o-visually-hidden">Open the command palette</span>
      </button>

      <button
        type="button"
        className={ageClass}
        onClick={onRefresh}
        title="Age of the change feed. Click to refresh what is on screen."
        style={{ background: 'none', border: 'none', cursor: 'pointer', minHeight: 'var(--tap-target-min)' }}
      >
        <span className="o-age__dot" aria-hidden="true" />
        <span>facts {ageText}</span>
      </button>

      <Link to="/inbox" className="o-topbar__bell" aria-label={`Action inbox, ${inboxCount} open`}>
        <span aria-hidden="true">IN</span>
        {inboxCount > 0 ? <span className="o-topbar__bell-count">{inboxCount}</span> : null}
      </Link>
    </header>
  );
}
